export default function mountNewsletterSignup({
	rootSelector = '.olt-newsletter',
	configSelector = '.olt-newsletter-config',
	endpoint,
	thankYou,
} = {}) {
	if (typeof document === 'undefined') {
		return;
	}

	const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
	const qs = (s, c = document) => c.querySelector(s);
	const parseJSON = (el) => {
		try {
			return JSON.parse(el?.textContent || '{}');
		} catch {
			return {};
		}
	};

	const j = async (url, opts = {}) => {
		const res = await fetch(url, { credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, ...opts });
		const body = await res.json().catch(() => ({}));
		return { ok: res.ok, status: res.status, body };
	};

	let token = '';

	const LIST_KEYS = ['opt_london_theatre_guide', 'opt_family_bulletin', 'opt_access_london_theatre'];

	const LIST_LABELS = {
		opt_london_theatre_guide: 'London Theatre Guide',
		opt_family_bulletin: 'Family Bulletin',
		opt_access_london_theatre: 'Access London Theatre'
	};

	// Access performance type preferences. Separate from LIST_KEYS so they do
	// not count towards the "choose at least one newsletter" requirement.
	// Ordered to match the checkboxes shown in the form.
	const ACCESS_KEYS = ['opt_access_audio_described', 'opt_access_bsl_interpreted', 'opt_access_captioned', 'opt_access_relaxed'];

	const ACCESS_LABELS = {
		opt_access_audio_described: 'Audio Described',
		opt_access_bsl_interpreted: 'British Sign Language',
		opt_access_captioned: 'Captioned',
		opt_access_relaxed: 'Relaxed'
	};

	// Publish lowercase campaign links, e.g. ?optin=familylist,mainlist.
	// Match Acoustic attribute names case-insensitively.
	// Only newsletter preferences are allowed; terms still require user input.
	const acousticAttributes = {
		MainList: 'opt_london_theatre_guide',
		FamilyList: 'opt_family_bulletin',
		Access_ALL: 'opt_access_london_theatre',
		Access_AudioDescribed: 'opt_access_audio_described',
		Access_BSLInterpreted: 'opt_access_bsl_interpreted',
		Access_Captioned: 'opt_access_captioned',
		Access_RelaxedChilledPerformances: 'opt_access_relaxed'
	};
	const preselectAttributes = new Map(
		Object.entries(acousticAttributes).map(([attribute, key]) => [attribute.toLowerCase(), key])
	);
	const preselected = new Set();
	const params = new URLSearchParams(window.location.search);
	const selections = params.getAll('optin');
	selections.forEach((value) => {
		value.split(',').forEach((choice) => {
			const key = preselectAttributes.get(choice.trim().toLowerCase());
			if (key) {
				preselected.add(key);
			}
		});
	});
	if (preselected.has('opt_access_london_theatre')) {
		ACCESS_KEYS.forEach((key) => preselected.add(key));
	}

	$$(rootSelector).forEach((root) => {
		const form = qs('.olt-newsletter__form', root);
		if (!form) {
			return;
		}

		const cfgEl = root.parentNode.querySelector(configSelector) || document.querySelector(configSelector);
		const cfg = parseJSON(cfgEl);
		const createApi = endpoint || cfg.endpoint || '/wp-json/subscribers/all';
		const thankUrl = thankYou || cfg.thankYou || '/newsletter-thank-you/';

		const msgEl = qs('.olt-newsletter__msg', root);
		const submitMsgEl = qs('.olt-newsletter__submit-msg', form);
		const submitBtn = qs('.olt-newsletter__submit, .olt-newsletter__submit, button[type="submit"]', form);
		const emailEl = qs('input[name="email"]', form);
		const termsEl = qs('input[name="accept_terms"]', form);
		const accessAllEl = qs('input[name="opt_access_london_theatre"]', form);

		preselected.forEach((key) => {
			const el = qs(`input[type="checkbox"][name="${ key }"]`, form);
			if (el) {
				el.checked = true;
			}
		});

		// When the Access Information option (Access_ALL) is toggled, mirror its
		// state across the individual access performance type checkboxes, so
		// selecting it opts the subscriber into every type at once.
		accessAllEl?.addEventListener('change', () => {
			ACCESS_KEYS.forEach((k) => {
				const el = qs(`input[name="${ k }"]`, form);
				if (el) {
					el.checked = accessAllEl.checked;
				}
			});
		});

		// If a user removes any individual access type, the Access Information
		// option no longer covers all types, so untick it to keep it accurate.
		ACCESS_KEYS.forEach((k) => {
			const el = qs(`input[name="${ k }"]`, form);
			el?.addEventListener('change', () => {
				if (!el.checked && accessAllEl && accessAllEl.checked) {
					accessAllEl.checked = false;
				}
			});
		});

		const showMsg = (t, ok) => {
			if (!msgEl) {
				return;
			}
			msgEl.textContent = t || 'Loading...';
			msgEl.classList.toggle('form-valid', !!ok);
			msgEl.classList.toggle('form-errors', !ok);
		};
		const showSubmitMsg = (text, ok) => {
			const el = submitMsgEl || msgEl;
			if (!el) {
				return;
			}
			el.textContent = text;
			el.classList.toggle('form-valid', ok === true);
			el.classList.toggle('form-errors', ok === false);
		};
		const setBusy = (b) => {
			if (submitBtn) {
				submitBtn.disabled = !!b;
			}
		};

		const initialCta = submitBtn?.textContent?.trim() || 'Sign up';
		const setSubmitLabel = (signedIn) => {
			if (!submitBtn) {
				return;
			}
			submitBtn.textContent = signedIn ? 'Update preferences' : initialCta;
		};
		setSubmitLabel(false);

		// ---------- Sign In ----------
		// Markup lives in the template; here we just wire up the controls.
		const panel = qs('.olt-newsletter__signin-panel', root);
		const tgl = qs('.olt-newsletter__signin-toggle', root);
		const sEmail = qs('.olt-newsletter__signin-email', root);
		const sBtn = qs('.olt-newsletter__signin-button', root);
		const unsubBtn = qs('.olt-newsletter__unsub-all', root);

		tgl?.addEventListener('click', () => {
			panel.hidden = !panel.hidden;
			if (!panel.hidden) {
				sEmail?.focus();
			}
		});

		sBtn?.addEventListener('click', async () => {
			const em = (sEmail?.value || '').trim();
			if (!/.+@.+\..+/.test(em)) {
				showMsg('Please enter a valid email', false); return;
			}

			setBusy(true); showMsg('Checking your subscription…', true);
			const { ok, body } = await j('/wp-json/subscriber-sessions/all', {
				method: 'POST',
				body: JSON.stringify({ email: em, newsletterType: 'static' })
			});
			setBusy(false);

			if (!ok || !body?.ok) {
				const hint = body?.message ? ` (${ String(body.message) })` : '';
				showMsg(`Sorry, something went wrong${ hint }.`, false);
				return;
			}
			if (!body.existing) {
				showMsg('We couldn’t find a subscription for that email. Please sign up.', false);
				return;
			}

			// hydrate + set token
			token = body.token || '';
			if (emailEl) {
				emailEl.value = body.email || em; emailEl.readOnly = true; emailEl.setAttribute('aria-readonly', 'true');
			}
			LIST_KEYS.forEach((k) => {
				const el = qs(`input[name="${ k }"]`, form);
				if (el) {
					el.checked = !!(body.lists?.[ k ]);
				}
			});
			ACCESS_KEYS.forEach((k) => {
				const el = qs(`input[name="${ k }"]`, form);
				if (el) {
					el.checked = !!(body.access?.[ k ]);
				}
			});

			// Tell the subscriber exactly what they are opted in to rather
			// than relying on them spotting the pre-ticked checkboxes.
			const subscribedTo = LIST_KEYS
				.filter((k) => (body.lists?.[ k ]))
				.map((k) => LIST_LABELS[ k ] || k);

			const subscribedAccess = ACCESS_KEYS
				.filter((k) => (body.access?.[ k ]))
				.map((k) => ACCESS_LABELS[ k ] || k);

			const summary = [];
			if (subscribedTo.length) {
				summary.push(`You are currently subscribed to: ${ subscribedTo.join(', ') }.`);
			}
			if (subscribedAccess.length) {
				summary.push(`Access performances: ${ subscribedAccess.join(', ') }.`);
			}

			if (body.opted_out) {
				// Acoustic holds an opt out against this contact, which
				// suppresses them whatever the preference fields say, so
				// report that rather than listing subscriptions they
				// cannot currently receive.
				showMsg('You are currently unsubscribed from all emails, so you will not receive anything from us. Tick what you\'d like to receive below and save to start again.', true);
			} else if (summary.length) {
				showMsg(`${ summary.join(' ') } Update your choices below and save.`, true);
			} else {
				showMsg('You are not currently subscribed to any newsletters. Tick what you\'d like to subscribe to below and save.', true);
			}

			panel.hidden = true;
			if (unsubBtn) {
				unsubBtn.hidden = body.opted_out;
			}
			setSubmitLabel(true);
		});

		// ---------- one-click unsubscribe from everything ----------
		unsubBtn?.addEventListener('click', async () => {
			if (!token) {
				return;
			}
			if (!window.confirm('Are you sure you want to unsubscribe from all newsletters?')) {
				return;
			}

			unsubBtn.disabled = true;
			setBusy(true);
			showMsg('Unsubscribing…', true);

			const qp = new URLSearchParams({ subscriberToken: token, newsletterType: 'static' }).toString();
			const { ok, body } = await j(`/wp-json/subscribers/bytoken?${ qp }`, { method: 'DELETE' });

			unsubBtn.disabled = false;
			setBusy(false);

			if (ok && body?.ok) {
				LIST_KEYS.forEach((k) => {
					const el = qs(`input[name="${ k }"]`, form);
					if (el) {
						el.checked = false;
					}
				});
				ACCESS_KEYS.forEach((k) => {
					const el = qs(`input[name="${ k }"]`, form);
					if (el) {
						el.checked = false;
					}
				});
				showMsg('You have been unsubscribed from all newsletters.', true);
			} else {
				showMsg('Sorry, we couldn’t unsubscribe you. Please try again.', false);
			}
		});

		// ---------- deep link: open the manage preferences panel ----------
		// Other pages can link straight to the manage flow with ?manage=1
		// or #manage-preferences appended to this page's URL.
		const wantsManage = new URLSearchParams(window.location.search).has('manage') ||
			window.location.hash === '#manage-preferences';
		if (wantsManage && panel) {
			panel.hidden = false;
			root.scrollIntoView({ block: 'start' });
			sEmail?.focus();
		}

		// ---------- submit: create vs update (uses token if present) ----------
		form.addEventListener('submit', async (e) => {
			e.preventDefault();
			showSubmitMsg(''); setBusy(true);

			const email = (emailEl?.value || '').trim();
			const accepted = !!(termsEl && termsEl.checked);

			const lists = LIST_KEYS.filter((k) => qs(`input[name="${ k }"]`, form)?.checked);
			const accessLists = ACCESS_KEYS.filter((k) => qs(`input[name="${ k }"]`, form)?.checked);

			if (!token && !lists.length && !accessLists.length) {
				showSubmitMsg('Choose at least one newsletter', false); setBusy(false); return;
			}

			if (!token) {
				if (!/.+@.+\..+/.test(email)) {
					showSubmitMsg('Please enter a valid email', false); setBusy(false); return;
				}
				if (!accepted) {
					showSubmitMsg('Please accept the terms', false); setBusy(false); return;
				}
			}

			const payload = { newsletterType: 'static', accept_terms: true };
			if (!token) {
				payload.email = email; payload.first_name = '';
			}
			LIST_KEYS.forEach((k) => {
				const el = qs(`input[name="${ k }"]`, form);
				if (el) {
					payload[ k ] = !!el.checked;
				}
			});

			ACCESS_KEYS.forEach((k) => {
				const el = qs(`input[name="${ k }"]`, form);
				if (el) {
					payload[ k ] = !!el.checked;
				}
			});

			showSubmitMsg(token ? 'Saving your preferences…' : 'Signing you up…');
			if (submitBtn) {
				submitBtn.textContent = token ? 'Saving…' : 'Signing up…';
			}

			try {
				if (token) {
					const qp = new URLSearchParams({ subscriberToken: token, newsletterType: 'static' }).toString();
					const { ok, body } = await j(`/wp-json/subscribers/bytoken?${ qp }`, {
						method: 'PATCH',
						body: JSON.stringify(payload)
					});
					const saved = !!(ok && body?.ok);
					let message = 'Sorry, we couldn’t save your preferences.';
					if (saved) {
						// The API reports when a save cleared every option,
						// so the confirmation matches what actually happened.
						message = body?.opted_out
							? 'You have been unsubscribed from all newsletters.'
							: 'Preferences saved.';
					}
					showSubmitMsg(message, saved);
				} else {
					const { ok, body } = await j(createApi, { method: 'POST', body: JSON.stringify(payload) });
					if (ok && body?.ok) {
						window.location.href = thankUrl; return;
					}
					showSubmitMsg('Sorry, something went wrong. Please try again.', false);
				}
			} catch {
				showSubmitMsg('Network error. Please try again.', false);
			} finally {
				setBusy(false);
				setSubmitLabel(!!token);
			}
		});
	});
}
